import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import { initMfaDeviceReplace, confirmMfaDeviceReplace } from '../../api';
import './MfaDeviceReplaceModal.css';

const STEP_PASSWORD = 'password';
const STEP_CONFIGURE = 'configure';
const STEP_RECOVERY_CODES = 'recovery_codes';

const MfaDeviceReplaceModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(STEP_PASSWORD);
  const [password, setPassword] = useState('');
  const [candidateSecret, setCandidateSecret] = useState('');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [replacementToken, setReplacementToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [showManualKey, setShowManualKey] = useState(false);
  const [hasAcknowledgedCodes, setHasAcknowledgedCodes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  // Reset all state when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setStep(STEP_PASSWORD);
      setPassword('');
      setCandidateSecret('');
      setProvisioningUri('');
      setReplacementToken('');
      setTotpCode('');
      setRecoveryCodes([]);
      setShowManualKey(false);
      setHasAcknowledgedCodes(false);
      setIsSubmitting(false);
      setErrorMessage('');
      setInfoMessage('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (isSubmitting) return;
    // Do not allow accidental close on the one-time recovery codes screen without saving
    if (step === STEP_RECOVERY_CODES && !hasAcknowledgedCodes) {
      setErrorMessage('Please save your recovery codes and check the confirmation box before closing.');
      return;
    }
    // Wipe all sensitive candidate values in memory
    setPassword('');
    setCandidateSecret('');
    setProvisioningUri('');
    setReplacementToken('');
    setTotpCode('');
    setRecoveryCodes([]);
    onClose();
  };

  // Step A: Submit Current Password to Initialize Replacement
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage('');
    setInfoMessage('');

    try {
      const data = await initMfaDeviceReplace(password);
      // Backend returns: secret, provisioning_uri, replacement_token, expires_in, detail
      setCandidateSecret(data.secret || '');
      setProvisioningUri(data.provisioning_uri || '');
      setReplacementToken(data.replacement_token || '');
      // Clear password from state immediately
      setPassword('');
      setStep(STEP_CONFIGURE);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to verify password. Please try again.';
      setErrorMessage(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step C: Confirm Replacement with New TOTP Code
  const handleConfirmSubmit = async (e) => {
    e.preventDefault();
    const cleanCode = totpCode.trim();
    if (cleanCode.length !== 6 || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage('');
    setInfoMessage('');

    try {
      const data = await confirmMfaDeviceReplace(replacementToken, cleanCode);
      // Backend returns: detail, recovery_codes
      const returnedCodes = Array.isArray(data.recovery_codes) ? data.recovery_codes : [];
      // Wipe candidate secret and token from memory
      setCandidateSecret('');
      setProvisioningUri('');
      setReplacementToken('');
      setTotpCode('');

      setRecoveryCodes(returnedCodes);
      setStep(STEP_RECOVERY_CODES);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Verification failed. Please check the code and try again.';
      setErrorMessage(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy Secret Key to Clipboard
  const handleCopySecret = () => {
    if (!candidateSecret) return;
    navigator.clipboard.writeText(candidateSecret).then(() => {
      setInfoMessage('Account key copied to clipboard!');
      setTimeout(() => setInfoMessage(''), 3000);
    });
  };

  // Copy Recovery Codes to Clipboard
  const handleCopyRecoveryCodes = () => {
    if (recoveryCodes.length === 0) return;
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setInfoMessage('All recovery codes copied to clipboard!');
      setTimeout(() => setInfoMessage(''), 3000);
    });
  };

  // Download Recovery Codes as .txt
  const handleDownloadRecoveryCodes = () => {
    if (recoveryCodes.length === 0) return;
    const text =
      `SALESROUTE SUPERADMIN RECOVERY CODES\n` +
      `Generated: ${new Date().toISOString()}\n\n` +
      `Keep these 10 one-time recovery codes in a secure location.\n` +
      `Each code can only be used once.\n` +
      `Previous recovery codes are now INVALID.\n\n` +
      recoveryCodes.map((code, index) => `${index + 1}. ${code}`).join('\n') +
      `\n\nDO NOT SHARE THESE CODES WITH ANYONE.\n` +
      `Plaintext codes cannot be recovered from server hashes if lost.`;

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'salesroute-superadmin-recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Final Acknowledgement and Completion
  const handleCompleteReplacement = () => {
    if (!hasAcknowledgedCodes) return;
    // Wipe recovery codes from memory
    setRecoveryCodes([]);
    onSuccess();
  };

  return (
    <div className="mfa-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="mfa-replace-title">
      <div className="mfa-modal-container">
        {/* Header */}
        <div className="mfa-modal-header">
          <div className="mfa-modal-header-text">
            <h3 id="mfa-replace-title">
              {step === STEP_PASSWORD && 'Replace Authenticator App'}
              {step === STEP_CONFIGURE && 'Configure New Authenticator'}
              {step === STEP_RECOVERY_CODES && 'Save New Recovery Codes'}
            </h3>
            <p>
              {step === STEP_PASSWORD && 'Re-authenticate with your SuperAdmin password to begin'}
              {step === STEP_CONFIGURE && 'Scan the QR code and verify with your new authenticator'}
              {step === STEP_RECOVERY_CODES && 'Important: Store these one-time codes in a secure location'}
            </p>
          </div>
          {step !== STEP_RECOVERY_CODES && (
            <button
              type="button"
              className="mfa-modal-close-btn"
              onClick={handleClose}
              disabled={isSubmitting}
              aria-label="Close modal"
              data-testid="mfa-modal-close-btn"
            >
              ×
            </button>
          )}
        </div>

        {/* Body */}
        <div className="mfa-modal-body">
          {errorMessage && (
            <div className="mfa-modal-banner mfa-modal-banner-error" role="alert" data-testid="mfa-modal-error">
              {errorMessage}
            </div>
          )}

          {infoMessage && (
            <div className="mfa-modal-banner mfa-modal-banner-info" data-testid="mfa-modal-info">
              {infoMessage}
            </div>
          )}

          {/* STEP 1: Password Confirmation */}
          {step === STEP_PASSWORD && (
            <form onSubmit={handlePasswordSubmit} data-testid="mfa-password-form">
              <div className="mfa-modal-banner mfa-modal-banner-info">
                <strong>Active device protection:</strong> Your current authenticator app remains fully active until you successfully verify your new authenticator.
              </div>

              <div className="mfa-input-group mt-15">
                <label className="mfa-input-label" htmlFor="mfa-current-password">
                  Current SuperAdmin Password
                </label>
                <input
                  id="mfa-current-password"
                  type="password"
                  className="mfa-text-input"
                  placeholder="Enter current password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  autoFocus
                  required
                  data-testid="mfa-current-password-input"
                />
              </div>

              <div className="mfa-modal-footer">
                <button
                  type="button"
                  className="mfa-btn-secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  data-testid="mfa-password-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="mfa-btn-primary"
                  disabled={isSubmitting || !password.trim()}
                  data-testid="mfa-password-submit-btn"
                >
                  {isSubmitting ? 'Verifying...' : 'Continue to QR Code'}
                </button>
              </div>
            </form>
          )}

          {/* STEP 2: Configure & Verify New Authenticator */}
          {step === STEP_CONFIGURE && (
            <form onSubmit={handleConfirmSubmit} data-testid="mfa-configure-form">
              <div className="mfa-modal-banner mfa-modal-banner-warning">
                <strong>Safety Notice:</strong> Do not remove or delete your existing authenticator app until this new device has been verified and confirmed.
              </div>

              <div className="mfa-qr-section">
                <p className="mfa-qr-instructions">
                  Scan this QR code using Google Authenticator, Authy, or your preferred TOTP app:
                </p>

                {provisioningUri ? (
                  <div className="mfa-qr-card" data-testid="mfa-qr-card">
                    <QRCodeSVG
                      value={provisioningUri}
                      size={180}
                      level="M"
                      includeMargin={true}
                    />
                  </div>
                ) : (
                  <div className="mfa-modal-banner mfa-modal-banner-warning">
                    Provisioning URI not available. Use manual key entry below.
                  </div>
                )}

                {candidateSecret && (
                  <div className="w-100 text-center">
                    <button
                      type="button"
                      className="mfa-manual-key-toggle"
                      onClick={() => setShowManualKey(!showManualKey)}
                      data-testid="mfa-manual-key-toggle"
                    >
                      {showManualKey ? 'Hide manual account key' : "Can't scan QR code? Enter manual key"}
                    </button>

                    {showManualKey && (
                      <div className="mfa-manual-key-box" data-testid="mfa-manual-key-box">
                        <span className="fts-11 lt-gray">Account Key (Base32):</span>
                        <code className="mfa-manual-secret-code" data-testid="mfa-secret-code">
                          {candidateSecret}
                        </code>
                        <button
                          type="button"
                          className="mfa-copy-btn"
                          onClick={handleCopySecret}
                          data-testid="mfa-copy-secret-btn"
                        >
                          Copy Key
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mfa-totp-verify-section">
                <label className="mfa-input-label text-center d-block" htmlFor="mfa-totp-code-input">
                  Enter 6-Digit Code from New Authenticator
                </label>
                <input
                  id="mfa-totp-code-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  className="mfa-totp-code-input"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={isSubmitting}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  data-testid="mfa-totp-code-input"
                />
              </div>

              <div className="mfa-modal-footer">
                <button
                  type="button"
                  className="mfa-btn-secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  data-testid="mfa-configure-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="mfa-btn-primary"
                  disabled={isSubmitting || totpCode.trim().length !== 6}
                  data-testid="mfa-confirm-submit-btn"
                >
                  {isSubmitting ? 'Confirming...' : 'Confirm & Replace Device'}
                </button>
              </div>
            </form>
          )}

          {/* STEP 3: Mandatory One-Time Recovery Codes */}
          {step === STEP_RECOVERY_CODES && (
            <div className="mfa-recovery-codes-section" data-testid="mfa-recovery-codes-section">
              <div className="mfa-modal-banner mfa-modal-banner-success">
                <strong>Authenticator successfully replaced!</strong> Your new device is now active. All other active sessions have been revoked.
              </div>

              <div className="mfa-modal-banner mfa-modal-banner-warning">
                <strong>CRITICAL:</strong> Below are your 10 new emergency recovery codes. All previous recovery codes are now invalid. These codes are displayed <u>ONLY ONCE</u> and cannot be recovered from server hashes if lost.
              </div>

              <div className="mfa-recovery-codes-grid" data-testid="mfa-new-recovery-codes-grid">
                {recoveryCodes.map((code, index) => (
                  <div key={index} className="mfa-recovery-code-card">
                    <span className="mfa-code-num">{index + 1}.</span>
                    <span className="mfa-code-txt">{code}</span>
                  </div>
                ))}
              </div>

              <div className="mfa-recovery-code-actions">
                <button
                  type="button"
                  className="mfa-recovery-action-btn"
                  onClick={handleCopyRecoveryCodes}
                  data-testid="mfa-copy-all-codes-btn"
                >
                  📋 Copy All Codes
                </button>
                <button
                  type="button"
                  className="mfa-recovery-action-btn"
                  onClick={handleDownloadRecoveryCodes}
                  data-testid="mfa-download-codes-btn"
                >
                  💾 Download as .txt
                </button>
              </div>

              <label className="mfa-acknowledgement-box" data-testid="mfa-acknowledgement-box">
                <input
                  type="checkbox"
                  checked={hasAcknowledgedCodes}
                  onChange={(e) => setHasAcknowledgedCodes(e.target.checked)}
                  data-testid="mfa-acknowledge-checkbox"
                />
                <span>
                  I have saved these 10 recovery codes in a secure location and understand they cannot be viewed again.
                </span>
              </label>

              <div className="mfa-modal-footer">
                <button
                  type="button"
                  className="mfa-btn-primary w-100"
                  disabled={!hasAcknowledgedCodes}
                  onClick={handleCompleteReplacement}
                  data-testid="mfa-complete-ack-btn"
                >
                  I Have Saved My Codes — Return to Profile
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

MfaDeviceReplaceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default MfaDeviceReplaceModal;
